import React, {useState} from 'react';
import styled from 'styled-components';
import SeverityEnum, {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
// import PerformanceTrack from 'features/POSRegisters/POSTable/Registers/components/PerformanceTrack';
import ScanDetails, {IpadScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';
import ModalIconTitle from 'features/POSRegisters/components/ModalContent/components/ModalTitle';
import PerformanceTrack from 'features/POSRegisters/POSTable/Registers/components/PerformanceTrack';
import DataRow, {DataColumn} from 'features/POSRegisters/POSDiagram/components/DataGrid/DataRow';
import ModalMainContentContainer from './components/ModalContentContainer';
import ModalTabs, {ModalTab} from './components/ModalTabs';
import ModalSubHeader from './components/ModalSubHeader';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import TabPanel from 'components/Tabs/TabPanel';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';
import {AdaptedDeviceData} from 'features/POSRegisters/interfaces/AdaptedDeviceData';

//#region Styled Components
const StyledModalContent = styled.div``;

const StyledDiskInformation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-grow: 1;
  width: 100%;
`;
//#endregion Styled Components

//#region Column Definitions
const connectivityColumns: DataColumn[] = [
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'macAddress',
    label: 'MAC Address',
    width: 'auto',
    minWidth: '140px',
  },
  {
    id: 'vlanNumber',
    label: 'VLAN',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'switchId',
    label: 'Switch ID',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'switchNumber',
    label: 'Switch Number',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'portNumber',
    label: 'Port Number',
    width: 'auto',
    minWidth: '100px',
  },
];

const connectivityCheckInColumns: DataColumn[] = [
  {
    id: 'mobileApiLastSeen',
    label: 'Mobile Api Last Seen',
    width: 'auto',
    minWidth: '220px',
  },
  {
    id: 'registerFirstSeenDate',
    label: 'Register First Seen Date',
    width: 'auto',
    minWidth: '220px',
  },
  {
    id: 'registerLastSeenDate',
    label: 'Register Last Seen Date',
    width: 'auto',
    minWidth: '220px',
  },
];

const informationNposColumns: DataColumn[] = [
  {
    id: 'registerAppVersion',
    label: 'Version',
    width: 'auto',
    minWidth: '220px',
  },
  {
    id: 'workstationNumber',
    label: 'Workstation Number',
    width: 'auto',
    minWidth: '220px',
  },
];

const informationOsColumns: DataColumn[] = [
  {
    id: 'iosVersion',
    label: 'iOS Version',
    width: 'auto',
    minWidth: '180px',
  },
];

const informationHardwareColumns: DataColumn[] = [
  {
    id: 'deviceName',
    label: 'Device Name',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'manufacturer',
    label: 'Manufacturer',
    width: 'auto',
    minWidth: '90px',
  },
  {
    id: 'model',
    label: 'Model',
    width: 'auto',
    minWidth: '180px',
  },
  {
    id: 'macAddress',
    label: 'Primary Mac Address',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'serialNumber',
    label: 'Serial Number',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '130px',
  },
];
//#endregion Column Definitions

//#region Component
interface IPadModalContent {
  deviceType: DeviceType;
  adaptedDeviceData: AdaptedDeviceData<IpadScanDetails> | null;
}

export default function IPadModalContent({deviceType, adaptedDeviceData}: IPadModalContent) {
  const [activeTab, setActiveTab] = useState<ModalTab>(ModalTab.CONNECTIVITY);

  if (!adaptedDeviceData) {
    return <div>No device data available</div>;
  }
  const ipad = adaptedDeviceData?.ipad;
  const scanDetails = ipad?.scanResults as ScanDetails;
  const alerts = ipad?.alerts;
  const severity = getPriorityAlert([ipad]);
  const hasIssues = Number(SeverityPriorityEnum[severity.toUpperCase() as any]) > 1;

  const ipadTabs: {tab: ModalTab; severity?: SeverityEnum; isValid?: boolean}[] = [
    {
      tab: ModalTab.CONNECTIVITY,
      severity: getPriorityAlert([
        {
          scanResults: ipad.scanResults,
          alerts: getSubTypeAlerts(alerts, [
            AlertScanSubType.CONNECTIVITY,
            AlertScanSubType.CHECK_IN_STATUS,
          ]),
        },
      ]),
      isValid: scanDetails !== undefined,
    },
    // {tab: ModalTab.PERFORMANCE, isValid: scanDetails !== undefined},
    {
      tab: ModalTab.INFORMATION,
      severity: getPriorityAlert([
        {
          scanResults: ipad.scanResults,
          alerts: getSubTypeAlerts(alerts, [
            AlertScanSubType.NPOS,
            AlertScanSubType.OPERATING_SYSTEM,
            AlertScanSubType.HARDWARE,
          ]),
        },
      ]),
      isValid: scanDetails !== undefined,
    },
  ];

  const handleTabChange = (tab: ModalTab) => {
    setActiveTab(tab);
  };

  return (
    <StyledModalContent>
      <ModalIconTitle
        deviceType={deviceType}
        severity={hasIssues ? severity : undefined}
        deviceName={adaptedDeviceData?.id}
      >
        <ModalTabs
          handleTabChange={handleTabChange}
          activeTabs={ipadTabs}
          selectedTab={activeTab}
        />
      </ModalIconTitle>

      <ModalMainContentContainer>
        <TabPanel value={activeTab} index={ModalTab.CONNECTIVITY}>
          <ModalSubHeader>CONNECTIVITY</ModalSubHeader>
          <DataRow
            columns={connectivityColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.CONNECTIVITY)}
          />
          <ModalSubHeader>CHECK-IN STATUS</ModalSubHeader>
          <DataRow
            columns={connectivityCheckInColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.CHECK_IN_STATUS)}
          />
        </TabPanel>
        {/* <TabPanel value={activeTab} index={ModalTab.PERFORMANCE}>
          <ModalSubHeader>DISK</ModalSubHeader>
          <StyledDiskInformation>
            <SGBody className="b2">Macintosh HD (Boot Partition)</SGBody>
            <SGBody className="b2">14.56 GB out of 64 GB (22%)</SGBody>
          </StyledDiskInformation>
          <PerformanceTrack value={40} />
        </TabPanel> */}
        <TabPanel value={activeTab} index={ModalTab.INFORMATION}>
          <ModalSubHeader>NPOS</ModalSubHeader>
          <DataRow
            columns={informationNposColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.NPOS)}
          />
          <ModalSubHeader>OPERATING SYSTEM</ModalSubHeader>
          <DataRow
            columns={informationOsColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.OPERATING_SYSTEM)}
          />
          <ModalSubHeader>HARDWARE</ModalSubHeader>
          <DataRow
            columns={informationHardwareColumns}
            data={scanDetails}
            alerts={getSubTypeAlerts(alerts, AlertScanSubType.HARDWARE)}
          />
        </TabPanel>
      </ModalMainContentContainer>
    </StyledModalContent>
  );
}
//#endregion Component
